/**
 * Professor Claim Wizard - Alpine.js Component
 * Full-page wizard for submitting professor claim requests
 */

import { wizardComponent } from '../../../../shared/components/wizard.js';

export default function professorClaimWizard(config = {}) {
    const baseWizard = wizardComponent({
        totalSteps: 3,
        currentStep: 1,
        draftKey: config.draftKey || `professor_claim_${config.professorId}`,
        autoSaveInterval: 30000
    });

    return {
        ...baseWizard,

        // Configuration
        professorId: config.professorId || null,
        professor: config.professor || null,
        existingClaim: config.existingClaim || null,
        currentUser: config.currentUser || null,
        isNameFromProfile: config.isNameFromProfile || false,

        // Form Fields - Step 1: Personal Information
        full_name: config.currentUser?.full_name || 
                   (config.currentUser ? `${config.currentUser.first_name || ''} ${config.currentUser.last_name || ''}`.trim() : ''),
        national_code: '',
        email: config.currentUser?.email || '',

        // Form Fields - Step 2: Message
        message: '',

        // Form Fields - Step 3: Document Upload
        professor_card: null,
        professor_card_preview: null,

        // Override init
        init() {
            // Call base wizard init
            baseWizard.init.call(this);

            // Initialize form data
            this.formData = this.getFormData();

            // Pre-fill user data if authenticated (only if not already set from config)
            if (this.currentUser && !this.full_name) {
                // Use full_name if available, otherwise construct from first_name and last_name
                this.full_name = this.currentUser.full_name || 
                    `${this.currentUser.first_name || ''} ${this.currentUser.last_name || ''}`.trim();
            }
            
            if (this.currentUser && !this.email) {
                this.email = this.currentUser.email || '';
            }

            // Load existing claim data if editing (this will override pre-filled data)
            if (this.existingClaim) {
                this.loadExistingClaim();
            }
        },

        // Get form data for draft saving
        getFormData() {
            return {
                full_name: this.full_name,
                national_code: this.national_code,
                email: this.email,
                message: this.message,
                // Note: professor_card is not saved in draft (file objects can't be serialized)
            };
        },

        // Load existing claim data
        loadExistingClaim() {
            if (this.existingClaim) {
                this.full_name = this.existingClaim.full_name || this.full_name;
                this.national_code = this.existingClaim.national_code || this.national_code;
                this.email = this.existingClaim.email || this.email;
                this.message = this.existingClaim.message || this.message;
            }
        },

        // Validate step
        validateStep(step) {
            switch (step) {
                case 1:
                    // Personal information validation
                    if (!this.full_name || this.full_name.trim().length < 5) {
                        this.setErrors({ full_name: ['نام و نام خانوادگی باید حداقل 5 کاراکتر باشد'] });
                        return false;
                    }

                    if (!this.national_code || !/^\d{10}$/.test(this.national_code.replace(/[۰-۹]/g, (char) => {
                        return String.fromCharCode(char.charCodeAt(0) - '۰'.charCodeAt(0) + '0'.charCodeAt(0));
                    }))) {
                        this.setErrors({ national_code: ['کد ملی باید 10 رقم باشد'] });
                        return false;
                    }

                    if (!this.email || !this.isValidEmail(this.email)) {
                        this.setErrors({ email: ['ایمیل معتبر وارد کنید'] });
                        return false;
                    }

                    this.clearErrors();
                    return true;

                case 2:
                    // Message validation
                    const messageLength = this.message.trim().length;
                    if (messageLength < 20) {
                        this.setErrors({ message: ['پیام باید حداقل 20 کاراکتر باشد'] });
                        return false;
                    }

                    if (messageLength > 1000) {
                        this.setErrors({ message: ['پیام نباید بیش از 1000 کاراکتر باشد'] });
                        return false;
                    }

                    this.clearErrors();
                    return true;

                case 3:
                    // Document upload validation
                    if (!this.professor_card) {
                        this.setErrors({ professor_card: ['ارسال کارت استادی الزامی است'] });
                        return false;
                    }

                    // Validate file type
                    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
                    if (!allowedTypes.includes(this.professor_card.type)) {
                        this.setErrors({ professor_card: ['فرمت فایل باید PDF، JPG، JPEG یا PNG باشد'] });
                        return false;
                    }

                    // Validate file size (5MB)
                    if (this.professor_card.size > 5 * 1024 * 1024) {
                        this.setErrors({ professor_card: ['حجم فایل نباید بیش از 5 مگابایت باشد'] });
                        return false;
                    }

                    this.clearErrors();
                    return true;

                default:
                    return true;
            }
        },

        // Handle file selection
        handleFileSelect(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Validate file type
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                this.setErrors({ professor_card: ['فرمت فایل باید PDF، JPG، JPEG یا PNG باشد'] });
                event.target.value = '';
                return;
            }

            // Validate file size (5MB)
            if (file.size > 5 * 1024 * 1024) {
                this.setErrors({ professor_card: ['حجم فایل نباید بیش از 5 مگابایت باشد'] });
                event.target.value = '';
                return;
            }

            // Store file
            this.professor_card = file;
            this.clearErrors();

            // Create preview for images
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.professor_card_preview = e.target.result;
                };
                reader.readAsDataURL(file);
            } else {
                // For PDF, just show file name
                this.professor_card_preview = null;
            }
        },

        // Remove file
        removeFile() {
            this.professor_card = null;
            this.professor_card_preview = null;
            const input = this.$refs.fileInput;
            if (input) {
                input.value = '';
            }
        },

        // Get file name
        getFileName() {
            return this.professor_card ? this.professor_card.name : '';
        },

        // Get file size in MB
        getFileSize() {
            if (!this.professor_card) return 0;
            return (this.professor_card.size / (1024 * 1024)).toFixed(2);
        },

        // Utility: Validate email
        isValidEmail(email) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(email);
        },

        // Utility: Get CSRF token
        getCsrfToken() {
            return document.querySelector('meta[name="csrf-token"]')?.content || '';
        },

        // Convert Persian/Arabic digits to English
        convertDigits(str) {
            if (!str) return '';
            const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
            const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
            let result = str;
            persianDigits.forEach((digit, index) => {
                result = result.replace(new RegExp(digit, 'g'), index);
            });
            arabicDigits.forEach((digit, index) => {
                result = result.replace(new RegExp(digit, 'g'), index);
            });
            return result;
        },

        // Custom submit handler (called by base wizard)
        async onSubmit(formDataObj) {
            // Prepare FormData for file upload
            const formData = new FormData();

            // Append all fields
            formData.append('full_name', this.full_name);
            formData.append('national_code', this.convertDigits(this.national_code));
            formData.append('email', this.email);
            formData.append('message', this.message);

            // Append file
            if (this.professor_card) {
                formData.append('professor_card', this.professor_card);
            }

            // Submit
            try {
                // Ensure CSRF token is in form data
                const csrfToken = this.getCsrfToken();
                if (csrfToken) {
                    formData.append('_token', csrfToken);
                }

                const response = await fetch(`/professors/${this.professorId}/claim`, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'X-CSRF-TOKEN': csrfToken,
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'application/json'
                    },
                    credentials: 'same-origin'
                });

                const data = await response.json();

                if (response.ok && data.success !== false) {
                    this.showSuccessMessage(
                        data.message || 'درخواست شما با موفقیت ارسال شد. نتیجه بررسی طی 24-48 ساعت آینده اعلام خواهد شد.'
                    );

                    // Dispatch event
                    window.dispatchEvent(new CustomEvent('claim-submitted', {
                        detail: {
                            success: true,
                            message: this.successMessage,
                            claimId: data.claim_id || data.claimId
                        }
                    }));

                    // Redirect after delay
                    setTimeout(() => {
                        window.location.href = `/professors/${this.professorId}`;
                    }, 2000);

                    return true;
                } else {
                    // Handle validation errors
                    if (data.errors) {
                        this.setErrors(data.errors);
                    } else {
                        this.setErrors({ general: [data.message || 'خطا در ارسال درخواست'] });
                    }
                    const error = new Error(data.message || 'خطا در ارسال درخواست');
                    error.errors = data.errors;
                    throw error;
                }
            } catch (error) {
                console.error('Claim submission error:', error);
                if (!error.errors) {
                    this.setErrors({ general: ['خطا در ارسال درخواست. لطفاً مجدداً تلاش کنید.'] });
                }
                throw error;
            }
        }
    };
}

